export type PageType =
  | "subscriptions"
  | "products"
  | "invs"
  | "cards"
  | "mandts";

export type GridPageType = Extract<PageType, "subscriptions" | "products">;

export interface RowHeader<T extends Record<string, any>> {
  displayName: string; // The string to be displayed in the table heading
  key: keyof T; // The key sent from the backend whose value holds the row information for the associated displayName heading on the table.
}

export interface RowData {
  Number?: string;
  Date?: string;
  Total?: string;
  Status?: string;
  Balance?: string;
  Card?: string;
  Expiry_Date?: string;
  Type?: string;
  Action?: string;
  First_Name?: string;
  Last_Name?: string;
  ID_Number?: string;
  Bank_Acc_Num?: string;
  Branch_Code?: string;
  Acc_Type?: string;
  Invoice_ID?: string;
  last_four_digits: string;
  expiry_year: string;
  expiry_month: string;
  card_id: string;
  mandate_name: string;
  recurring_amount: string;
  state: string;
  reference: string;
}

export interface ThProps {
  children: React.ReactNode;
  reversed: boolean;
  sorted: boolean;
  onSort: () => void;
}

export interface TrProps<T extends Record<string, any>> {
  /**
   * One row’s worth of data.
   * T can be e.g. { name: string; age: number } or { sku: string; price: number; stock: number }
   */
  row: T;

  /**
   * The exact list of keys (in order!) you want to render for this table.
   * Must all be keys of T.
   */
  headers: RowHeader<T>[];

  type: string; // The type of the table, e.g. "invs", "mandts", "cards"
}
export interface ButtonRowProps<T extends Record<string, any>> {
  /**
   * One row’s worth of data.
   * T can be e.g. { name: string; age: number } or { sku: string; price: number; stock: number }
   */
  row: T;
  type: string; // The type of the table, e.g. "invs", "mandts", "cards"
}

export interface SubscriptionProps {
  type: "subscriptions";
  data: SubsData;
  image: { alt: string; src: string };
}

export interface ProductProps {
  type: "products";
  data: ProductData;
  image: { alt: string; src: string };
}

export type GridItemProps = SubscriptionProps | ProductProps;

export interface MandateFormValues {
  first_name: string;
  last_name: string;
  email: string;
  bank_name: string;
  bank_account_number: string;
  bank_account_branch_code: string;
  bank_account_type: string;
  bank_account_holder_type: string;
  bank_account_holder_name: string;
  bank_account_holder_id: string;
  phone_dial_code: string;
  phone_dial_number: string;
  commences_at: string;
  currency_code: string;
  recurring_amount: number;
};

export interface SubsData {
  Subscription_Number: string;
  Subscription_ID: string;
  Name: string;
  Currency_Symbol: string;
  Plan_Name?: string;
  Plan_Code?: string;
  Total: string;
  Status?: string;
  Last_Billing_Date?: string;
  Next_Billing_Date?: string;
  card?: CardData;
}

export interface ProductData {
  Name: string;
  Product_Id: string;
  Description?: string;
  Status?: string;
}

export interface ProductPlansData {
  Name: string;
  Plan_Code: string;
  Recurring_Price: string;
  Status: string;
  Interval_Unit: string;
  Billing_Cycles: string;
  Url: string;
  ImageURL?: string;
}

export interface MerchData {
  active: boolean;
  name: string;
  id: string;
  logo_url: string;
}

export const dialCodes = [
  { value: "+27", label: "🇿🇦 South Africa (+27)" },
  { value: "+1", label: "🇺🇸 United States (+1)" },
  { value: "+44", label: "🇬🇧 United Kingdom (+44)" },
  { value: "+91", label: "🇮🇳 India (+91)" },
  { value: "+61", label: "🇦🇺 Australia (+61)" },
  { value: "+81", label: "🇯🇵 Japan (+81)" },
  { value: "+49", label: "🇩🇪 Germany (+49)" },
  { value: "+33", label: "🇫🇷 France (+33)" },
  { value: "+39", label: "🇮🇹 Italy (+39)" },
  { value: "+34", label: "🇪🇸 Spain (+34)" },
  { value: "+86", label: "🇨🇳 China (+86)" },
  { value: "+7", label: "🇷🇺 Russia (+7)" },
  { value: "+55", label: "🇧🇷 Brazil (+55)" },
  { value: "+64", label: "🇳🇿 New Zealand (+64)" },
  { value: "+82", label: "🇰🇷 South Korea (+82)" },
  { value: "+971", label: "🇦🇪 UAE (+971)" },
  { value: "+966", label: "🇸🇦 Saudi Arabia (+966)" },
  { value: "+234", label: "🇳🇬 Nigeria (+234)" },
  { value: "+20", label: "🇪🇬 Egypt (+20)" },
  { value: "+92", label: "🇵🇰 Pakistan (+92)" },
  { value: "+880", label: "🇧🇩 Bangladesh (+880)" },
  { value: "+62", label: "🇮🇩 Indonesia (+62)" },
  { value: "+63", label: "🇵🇭 Philippines (+63)" },
  { value: "+90", label: "🇹🇷 Turkey (+90)" },
  { value: "+212", label: "🇲🇦 Morocco (+212)" },
  { value: "+254", label: "🇰🇪 Kenya (+254)" },
  { value: "+256", label: "🇺🇬 Uganda (+256)" },
  { value: "+255", label: "🇹🇿 Tanzania (+255)" },
  { value: "+358", label: "🇫🇮 Finland (+358)" },
  { value: "+46", label: "🇸🇪 Sweden (+46)" },
  { value: "+47", label: "🇳🇴 Norway (+47)" },
  { value: "+48", label: "🇵🇱 Poland (+48)" },
  { value: "+351", label: "🇵🇹 Portugal (+351)" },
  { value: "+30", label: "🇬🇷 Greece (+30)" },
  { value: "+31", label: "🇳🇱 Netherlands (+31)" },
  { value: "+32", label: "🇧🇪 Belgium (+32)" },
  { value: "+41", label: "🇨🇭 Switzerland (+41)" },
  { value: "+43", label: "🇦🇹 Austria (+43)" },
  { value: "+420", label: "🇨🇿 Czech Republic (+420)" },
  { value: "+421", label: "🇸🇰 Slovakia (+421)" },
  { value: "+36", label: "🇭🇺 Hungary (+36)" },
  { value: "+353", label: "🇮🇪 Ireland (+353)" },
  { value: "+52", label: "🇲🇽 Mexico (+52)" },
  { value: "+1-876", label: "🇯🇲 Jamaica (+1-876)" },
  { value: "+1-246", label: "🇧🇧 Barbados (+1-246)" },
  { value: "+1-268", label: "🇦🇬 Antigua & Barbuda (+1-268)" },
  { value: "+1-441", label: "🇧🇲 Bermuda (+1-441)" },
  { value: "+1-473", label: "🇬🇩 Grenada (+1-473)" },
  { value: "+1-664", label: "🇲🇸 Montserrat (+1-664)" },
  { value: "+1-758", label: "🇱🇨 Saint Lucia (+1-758)" },
  { value: "+1-784", label: "🇻🇨 Saint Vincent & Grenadines (+1-784)" },
  { value: "+1-868", label: "🇹🇹 Trinidad & Tobago (+1-868)" },
  { value: "+1-869", label: "🇰🇳 Saint Kitts & Nevis (+1-869)" }
];
export interface CardData {
  card_id: string;
  card_type: string;
  expiry_month: number;
  expiry_year: number;
  last_four_digits: string;

}

export interface MandateData {

  created_at: string;
  type: string;
  frequency: string;
  name: string;
  recurring_amount: string;
  mandate: {
    bank_account_number: string;
    bank_name: string;
    bank_account_type: string;
    bank_account_holder_name: string;

  };


}
